import { AlertDialog, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { AlertTriangle, FileText, ShieldCheck } from 'lucide-react';
import { Link } from 'react-router-dom';
import type { TaxPreviewResult } from '@/hooks/useTaxPreview';

interface PurchaseAgreementModalProps {
  open: boolean;
  onClose: () => void;
  onAccept: () => void;
  onReviewDetails: () => void;
  amount: number; // Amount in cents (base PO amount)
  loading?: boolean;
  taxPreview?: TaxPreviewResult | null;
}

export function PurchaseAgreementModal({ 
  open, 
  onClose, 
  onAccept,
  onReviewDetails,
  amount,
  loading = false,
  taxPreview,
}: PurchaseAgreementModalProps) {
  const formatCurrency = (cents: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(cents / 100);
  };

  const taxAmount = taxPreview?.totalTaxCents ?? 0;
  const hasTax = taxPreview?.taxEnabled && taxAmount > 0;
  const hasExemptions = taxPreview?.hasExemptions ?? false;
  const totalAmount = amount + taxAmount;

  return (
    <AlertDialog open={open} onOpenChange={onClose}>
      <AlertDialogContent className="max-w-xl">
        <AlertDialogHeader>
          <div className="flex items-center gap-3 mb-2">
            <FileText className="h-8 w-8 text-primary" />
            <AlertDialogTitle className="text-2xl">Confirm Purchase Agreement</AlertDialogTitle>
          </div>
          <AlertDialogDescription asChild>
            <div className="space-y-4 text-base text-muted-foreground">
              <p className="text-foreground font-medium">
                You are about to issue a legally binding purchase order for this job.
              </p>

              <div className="bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800 p-4 rounded-lg space-y-3">
                <p className="font-semibold text-blue-900 dark:text-blue-100">💳 Payment Authorization</p>
                
                {/* Payment Breakdown */}
                <div className="bg-white dark:bg-blue-900/50 p-3 rounded border border-blue-200 dark:border-blue-700 space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-blue-700 dark:text-blue-300">PO Amount</span>
                    <span className="text-blue-900 dark:text-blue-100">{formatCurrency(amount)}</span>
                  </div>
                  
                  {/* Tax breakdown by location */}
                  {hasTax && taxPreview?.locationBreakdowns && taxPreview.locationBreakdowns.length > 0 && (
                    <>
                      <Separator className="bg-blue-200 dark:bg-blue-700" />
                      <div className="space-y-1">
                        <p className="text-xs text-blue-600 dark:text-blue-400 font-medium">Sales Tax by Location:</p>
                        {taxPreview.locationBreakdowns.map((loc, idx) => (
                          <div key={idx} className="flex justify-between text-xs">
                            <span className="text-blue-700 dark:text-blue-300 flex items-center gap-1.5">
                              {loc.locationName || loc.state}
                              {loc.isExempt && (
                                <Badge variant="outline" className="text-[10px] py-0 px-1 border-green-300 text-green-700 dark:border-green-700 dark:text-green-400">
                                  <ShieldCheck className="h-2.5 w-2.5 mr-0.5" />
                                  Exempt
                                </Badge>
                              )}
                            </span>
                            <span className="text-blue-900 dark:text-blue-100">
                              {loc.isExempt ? '$0.00' : formatCurrency(loc.taxAmountCents)}
                            </span>
                          </div>
                        ))}
                      </div>
                    </>
                  )}

                  {/* Single tax line if no breakdown available */}
                  {hasTax && (!taxPreview?.locationBreakdowns || taxPreview.locationBreakdowns.length === 0) && (
                    <div className="flex justify-between text-sm">
                      <span className="text-blue-700 dark:text-blue-300">Sales Tax</span>
                      <span className="text-blue-900 dark:text-blue-100">{formatCurrency(taxAmount)}</span>
                    </div>
                  )}
                  
                  <Separator className="bg-blue-200 dark:bg-blue-700" />
                  
                  <div className="flex justify-between">
                    <span className="text-blue-700 dark:text-blue-300 font-medium">Total:</span>
                    <span className="text-2xl font-bold text-blue-900 dark:text-blue-100">{formatCurrency(totalAmount)}</span>
                  </div>
                </div>
                
                <p className="text-blue-800 dark:text-blue-200 text-sm">
                  Your payment method will be <span className="font-bold">charged immediately</span>. 
                  Funds are held securely until you approve the completed work.
                </p>
                <p className="text-blue-700 dark:text-blue-300 text-xs">
                  Credit card payments include a 2.9% processing fee (shown on next step). Use ACH to avoid this fee.
                </p>
              </div>

              <div className="bg-orange-50 dark:bg-orange-950 border border-orange-200 dark:border-orange-800 p-4 rounded-lg">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="h-5 w-5 text-orange-600 dark:text-orange-400 mt-0.5 flex-shrink-0" />
                  <div className="space-y-2">
                    <p className="font-semibold text-orange-900 dark:text-orange-100">Important</p>
                    <p className="text-orange-800 dark:text-orange-200 text-sm">
                      Once issued, any changes or cancellations must follow the formal change request process 
                      and may result in additional fees or penalties. Please review all details before proceeding.
                    </p>
                  </div>
                </div>
              </div>

              <p className="text-sm text-muted-foreground">
                By clicking "Accept and Issue Purchase Order," you agree to our{' '}
                <Link 
                  to="/po-terms" 
                  target="_blank" 
                  className="text-primary hover:underline font-medium"
                >
                  Purchase Order Terms and Conditions
                </Link>.
              </p>
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter className="flex-col sm:flex-row gap-2">
          <Button 
            variant="outline" 
            onClick={onReviewDetails}
            disabled={loading}
            className="w-full sm:w-auto"
          >
            Review Job Details
          </Button>
          <Button 
            onClick={onAccept}
            disabled={loading}
            className="w-full sm:w-auto bg-green-600 hover:bg-green-700 text-white"
          >
            {loading ? 'Processing...' : 'Accept and Issue Purchase Order'}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
